/**
 * 用户任务属性组件
 * 包含任务分配配置、表单关联、任务调度等功能
 */
import React, { useState } from 'react';
import { Form, Button, Card, Space, Tag, Typography, DatePicker } from 'antd';
import {
  TeamOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  EditOutlined,
  ClearOutlined,
  UserOutlined,
  ApartmentOutlined,
  SettingOutlined,
  CodeOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import locale from 'antd/lib/date-picker/locale/zh_CN';
import FormSelector from '../FormSelector';
import UserAssignmentSelector from '../UserAssignmentSelector';

const { Text } = Typography;

const UserTaskProperties = ({ 
  form, 
  assignmentConfig, 
  setAssignmentConfig,
  taskSchedule,
  setTaskSchedule 
}) => {
  const [formSelectorVisible, setFormSelectorVisible] = useState(false);
  const [userAssignmentVisible, setUserAssignmentVisible] = useState(false);

  // 渲染任务分配配置卡片
  const renderAssignmentCard = () => {
    // 优先使用新格式的 assignmentConfig，如果没有则尝试从旧格式构建
    const currentAssignment = assignmentConfig || {
      assignee: form.getFieldValue('assignee'),
      candidateUsers: form.getFieldValue('candidateUsers') || [],
      candidateGroups: form.getFieldValue('candidateGroups') || [],
      assignmentType: 'specific_users',
      runtimeConditions: []
    };

    const hasAssignment = assignmentConfig || 
      currentAssignment.assignee || 
      (currentAssignment.candidateUsers && currentAssignment.candidateUsers.length > 0) || 
      (currentAssignment.candidateGroups && currentAssignment.candidateGroups.length > 0);

    const getAssignmentDetails = () => {
      if (!assignmentConfig) {
        // 显示旧格式数据
        const details = [];
        if (currentAssignment.assignee) {
          details.push(
            <Tag key="assignee" icon={<UserOutlined />} color="blue">
              执行人: {currentAssignment.assignee}
            </Tag>
          );
        }
        if (currentAssignment.candidateUsers && currentAssignment.candidateUsers.length > 0) {
          details.push(
            <Tag key="users" icon={<UserOutlined />} color="green">
              候选用户: {currentAssignment.candidateUsers.join(', ')}
            </Tag>
          );
        }
        if (currentAssignment.candidateGroups && currentAssignment.candidateGroups.length > 0) {
          details.push(
            <Tag key="groups" icon={<TeamOutlined />} color="orange">
              候选组: {currentAssignment.candidateGroups.join(', ')}
            </Tag>
          );
        }
        return details;
      }

      // 显示新格式数据
      const details = [];
      
      switch (assignmentConfig.type) {
        case 'static_user':
          if (assignmentConfig.users && assignmentConfig.users.length > 0) {
            assignmentConfig.users.forEach((user, index) => {
              details.push(
                <Tag key={`user-${index}`} icon={<UserOutlined />} color="blue">
                  {user.Name || user.name || user.Username || user.username}
                </Tag>
              );
            });
          }
          break;
          
        case 'static_role':
          if (assignmentConfig.roles && assignmentConfig.roles.length > 0) {
            assignmentConfig.roles.forEach((role, index) => {
              details.push(
                <Tag key={`role-${index}`} icon={<TeamOutlined />} color="green">
                  {role.Name || role.name}
                </Tag>
              );
            });
          }
          break;
          
        case 'static_dept':
          if (assignmentConfig.depts && assignmentConfig.depts.length > 0) {
            assignmentConfig.depts.forEach((dept, index) => {
              details.push(
                <Tag key={`dept-${index}`} icon={<ApartmentOutlined />} color="orange">
                  {dept.Name || dept.name}
                </Tag>
              );
            });
          }
          break;
          
        default:
          details.push(
            <Tag key="dynamic" icon={<SettingOutlined />} color="cyan">
              {assignmentConfig.label || '动态分配'}
            </Tag>
          );
      }
      
      return details;
    };

    return (
      <Card 
        size="small" 
        className="mb-4"
        title={
          <div className="flex items-center space-x-2">
            <TeamOutlined />
            <span>任务分配配置</span>
          </div>
        }
        extra={
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setUserAssignmentVisible(true)}
            >
              配置
            </Button>
            {hasAssignment && (
              <Button
                size="small"
                icon={<ClearOutlined />}
                onClick={() => {
                  // 清除新格式和旧格式的数据
                  form.setFieldsValue({ 
                    assignee: '',
                    candidateUsers: [],
                    candidateGroups: []
                  });
                  setAssignmentConfig(null);
                }}
              >
                清除
              </Button>
            )}
          </Space>
        }
      >
        {hasAssignment ? (
          <div>
            <div className="mb-2">
              <Text strong>
                {assignmentConfig ? assignmentConfig.label : '传统配置'}
              </Text>
              {assignmentConfig && assignmentConfig.isDynamic && (
                <Tag color="cyan" className="ml-2">动态分配</Tag>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {getAssignmentDetails()}
            </div>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">
            <UserOutlined className="text-2xl mb-2" />
            <div>暂未配置任务分配</div>
            <Text type="secondary" className="text-xs">
              点击上方"配置"按钮设置任务分配规则
            </Text>
          </div>
        )}
      </Card>
    );
  };

  // 渲染任务调度配置
  const renderTaskScheduleCard = () => {
    return (
      <Card 
        size="small" 
        className="mb-4"
        title={
          <div className="flex items-center space-x-2">
            <ClockCircleOutlined />
            <span>任务调度配置</span>
          </div>
        }
      >
        <Form.Item
          label="截止日期"
          name="due_date"
          className="mb-3"
        >
          <DatePicker
            showTime
            placeholder="选择截止日期"
            className="w-full"
            format="YYYY-MM-DD HH:mm:ss"
            locale={locale}
            onChange={(date) => {
              const isoDate = date ? date.toISOString() : null;
              setTaskSchedule(prev => ({
                ...prev,
                due_date: isoDate
              }));
            }}
          />
        </Form.Item>
        
        <Form.Item
          label="跟进日期"
          name="follow_up_date"
          className="mb-0"
        >
          <DatePicker
            showTime
            placeholder="选择跟进日期"
            className="w-full"
            format="YYYY-MM-DD HH:mm:ss"
            locale={locale}
            onChange={(date) => {
              const isoDate = date ? date.toISOString() : null;
              setTaskSchedule(prev => ({
                ...prev,
                follow_up_date: isoDate
              }));
            }}
          />
        </Form.Item>
      </Card>
    );
  };

  // 渲染表单关联配置
  const renderFormAssociationCard = () => {
    return (
      <Card 
        size="small" 
        className="mb-4"
        title={
          <div className="flex items-center space-x-2">
            <FileTextOutlined />
            <span>关联表单</span>
          </div>
        }
        extra={
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<EditOutlined />}
              onClick={() => setFormSelectorVisible(true)}
            >
              选择
            </Button>
            {form.getFieldValue('formKey') && (
              <Button
                size="small"
                icon={<ClearOutlined />}
                onClick={() => form.setFieldsValue({ formKey: '' })}
              >
                清除
              </Button>
            )}
          </Space>
        }
      >
        {form.getFieldValue('formKey') ? (
          <div>
            <Tag icon={<FileTextOutlined />} color="blue">
              {form.getFieldValue('formKey')}
            </Tag>
          </div>
        ) : (
          <div className="text-center py-4 text-gray-400">
            <FileTextOutlined className="text-2xl mb-2" />
            <div>暂未关联表单</div>
            <Text type="secondary" className="text-xs">
              点击上方"选择"按钮关联业务表单
            </Text>
          </div>
        )}
      </Card>
    );
  };

  return (
    <div>
      {renderAssignmentCard()}
      {renderTaskScheduleCard()}
      {renderFormAssociationCard()}
      
      {/* 表单选择器 */}
      <FormSelector
        visible={formSelectorVisible}
        onCancel={() => setFormSelectorVisible(false)}
        onSelect={(selectedForm) => {
          if (Array.isArray(selectedForm)) {
            // 多选模式
            const formKeys = selectedForm.map(form => form.Id).join(',');
            form.setFieldsValue({ formKey: formKeys });
          } else {
            // 单选模式
            form.setFieldsValue({ formKey: selectedForm.Id });
          }
          setFormSelectorVisible(false);
        }}
        selectedFormKey={form.getFieldValue('formKey')}
        multiple={false}
        defaultSelectedForms={[]}
      />
      
      {/* 用户分配选择器 */}
      <UserAssignmentSelector
        visible={userAssignmentVisible}
        value={assignmentConfig}
        onChange={(config) => {
          // 保存新格式的分配配置
          setAssignmentConfig(config);
          
          // 为了兼容性，同时更新旧格式的表单字段
          const legacyFields = {
            assignee: '',
            candidateUsers: [],
            candidateGroups: []
          };
          
          // 根据新格式转换为旧格式字段
          if (config.type === 'static_user' && config.users) {
            legacyFields.candidateUsers = config.users.map(user => user.Name || user.name || user.Username || user.username).filter(Boolean);
          } else if (config.type === 'static_role' && config.roles) {
            legacyFields.candidateGroups = config.roles.map(role => role.Name || role.name).filter(Boolean);
          }
          
          form.setFieldsValue(legacyFields);
        }}
        onCancel={() => setUserAssignmentVisible(false)}
        title="配置任务分配"
      />
    </div>
  );
};

export default UserTaskProperties;